import axios from 'axios';
import { emit } from '../utils/eventBus';

const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Sub-recursos anidados bajo /api/<parent>/<sub>/... que deben emitir como <sub>.
// Permite que un POST a /api/entidades/trabajadores/create emita topic "trabajadores".
const SUB_RESOURCES = new Set([
  'trabajadores', 'transportistas', 'vehiculos', 'datos-generales',
  'articulos', 'unidades-medida', 'tipo-cambio', 'formas-pago', 'ubigeos',
  'categorias', 'marcas', 'modelos', 'familias', 'lineas', 'grupos',
  'centros-costo', 'tipos-existencia', 'cuentas-contables', 'areas',
  'prioridades', 'tipos-documento', 'tipos-operacion', 'motivos-traslado',
  'sucursales', 'modulos', 'cuentas', 'siscont',
]);

function urlToTopic(url = '') {
  const path = url.split('?')[0].replace(/^\//, '');
  const parts = path.split('/');
  if (parts[0] !== 'api' || !parts[1]) return null;
  if (parts.length >= 3 && SUB_RESOURCES.has(parts[2])) return parts[2];
  return parts[1];
}

api.interceptors.response.use(
  (response) => {
    const method = (response.config?.method || '').toLowerCase();
    if (['post', 'put', 'delete', 'patch'].includes(method)) {
      const topic = urlToTopic(response.config?.url);
      if (topic) emit(topic, { method, url: response.config.url, data: response.data });
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const empresaService = {
  getAll:  () => api.get('/api/empresas/'),
  getById: (id) => api.get(`/api/empresas/${id}`),
  create:  (data) => api.post('/api/empresas/', data),
  tablasCopiables: () => api.get('/api/empresas/copia/tablas'),
  copiar:  (data) => api.post('/api/empresas/copia/ejecutar', data),
  plantillaInfo:    () => api.get('/api/empresas/plantilla/info'),
  aplicarPlantilla: (idEmp) => api.post(`/api/empresas/${idEmp}/aplicar-plantilla`),
  listarSedes:  (idEmp)          => api.get(`/api/empresas/${idEmp}/sucursales`),
  crearSede:    (idEmp, data)    => api.post(`/api/empresas/${idEmp}/sucursales`, data),
  editarSede:   (idEmp, id, data) => api.put(`/api/empresas/${idEmp}/sucursales/${id}`, data),
  eliminarSede: (idEmp, id)       => api.delete(`/api/empresas/${idEmp}/sucursales/${id}`),
};

export const authService = {
  getEmpresas: () => api.get('/api/auth/empresas'),
  getSucursales: (id_empresa) => api.get(`/api/auth/sucursales/${id_empresa}`),
  login: (data) => api.post('/api/auth/login', data),               // legacy
  loginCredenciales: (data) => api.post('/api/auth/login-credenciales', data),
  seleccionarEmpresa: (data, preauthToken) =>
    api.post('/api/auth/seleccionar-empresa', data, { headers: { Authorization: `Bearer ${preauthToken}` } }),
  finalizarLogin: (data, preauthToken) =>
    api.post('/api/auth/finalizar', data, { headers: { Authorization: `Bearer ${preauthToken}` } }),
  getMe: () => api.get('/api/auth/me'),
};

export const usuarioService = {
  getAll: () => api.get('/api/usuarios/'),
  getById: (id) => api.get(`/api/usuarios/${id}`),
  create: (data) => api.post('/api/usuarios/', data),
  update: (id, data) => api.put(`/api/usuarios/${id}`, data),
  delete: (id) => api.delete(`/api/usuarios/${id}`),
  changePassword: (id, newPassword) => api.put(`/api/usuarios/${id}/password`, { new_password: newPassword }),
  getPermisos:      (id)        => api.get(`/api/usuarios/${id}/permisos`),
  setPermisos:      (id, data)  => api.put(`/api/usuarios/${id}/permisos`, data),
  empresasDisponibles: ()       => api.get('/api/usuarios/empresas/disponibles'),
  sucursalesEmpresa: (idEmp)    => api.get(`/api/usuarios/empresas/${idEmp}/sucursales`),
};

// Servicio genérico para tablas simples (codigo + descripcion)
export const tablaService = (tabla) => ({
  getAll: () => api.get(`/api/tablas/${tabla}`),
  create: (data) => api.post(`/api/tablas/${tabla}`, data),
  update: (id, data) => api.put(`/api/tablas/${tabla}/${id}`, data),
  delete: (id) => api.delete(`/api/tablas/${tabla}/${id}`),
});

export const unidadMedidaService = {
  getAll: () => api.get('/api/tablas/unidades-medida/list'),
  create: (data) => api.post('/api/tablas/unidades-medida/create', data),
  update: (id, data) => api.put(`/api/tablas/unidades-medida/update/${id}`, data),
  delete: (id) => api.delete(`/api/tablas/unidades-medida/delete/${id}`),
};

export const tipoCambioService = {
  getAll: () => api.get('/api/tablas/tipo-cambio/list'),
  getByFecha: (fecha) => api.get(`/api/tablas/tipo-cambio/by-fecha/${fecha}`),
  create: (data) => api.post('/api/tablas/tipo-cambio/create', data),
  update: (id, data) => api.put(`/api/tablas/tipo-cambio/update/${id}`, data),
  consultarSunat: (anio, mes) => api.get('/api/tablas/tipo-cambio/consultar-sunat', { params: { anio, mes } }),
  importarSunat: (anio, mes) => api.post(`/api/tablas/tipo-cambio/importar-sunat?anio=${anio}&mes=${mes}`),
};

export const articuloService = {
  getAll: () => api.get('/api/tablas/articulos/list'),
  getById: (id) => api.get(`/api/tablas/articulos/detail/${id}`),
  create: (data) => api.post('/api/tablas/articulos/create', data),
  update: (id, data) => api.put(`/api/tablas/articulos/update/${id}`, data),
  delete: (id) => api.delete(`/api/tablas/articulos/delete/${id}`),
  getNextCodigo: () => api.get('/api/tablas/articulos/next-codigo'),
  // Búsqueda remota para AutoComplete (min 3 chars)
  search: (q, opts = {}) => api.get('/api/tablas/articulos/search', {
    params: { q: q || '', limit: opts.limit || 20, ids: (opts.ids || []).join(',') },
  }),
};

export const entidadService = {
  getAll: () => api.get('/api/entidades/list'),
  create: (data) => api.post('/api/entidades/create', data),
  update: (id, data) => api.put(`/api/entidades/update/${id}`, data),
  delete: (id) => api.delete(`/api/entidades/delete/${id}`),
  consultarRuc: (ruc) => api.get(`/api/entidades/consultar-ruc/${ruc}`),
};

export const trabajadorService = {
  getAll: () => api.get('/api/entidades/trabajadores/list'),
  create: (data) => api.post('/api/entidades/trabajadores/create', data),
  update: (id, data) => api.put(`/api/entidades/trabajadores/update/${id}`, data),
  delete: (id) => api.delete(`/api/entidades/trabajadores/delete/${id}`),
};

export const transportistaService = {
  getAll: () => api.get('/api/entidades/transportistas/list'),
  create: (data) => api.post('/api/entidades/transportistas/create', data),
  update: (id, data) => api.put(`/api/entidades/transportistas/update/${id}`, data),
  delete: (id) => api.delete(`/api/entidades/transportistas/delete/${id}`),
};

export const sucursalService = {
  getAll: () => api.get('/api/almacenes/sucursales/list'),
  create: (data) => api.post('/api/almacenes/sucursales/create', data),
  update: (id, data) => api.put(`/api/almacenes/sucursales/update/${id}`, data),
  delete: (id) => api.delete(`/api/almacenes/sucursales/delete/${id}`),
};

export const kardexService = {
  get: (params = {}) => api.get('/api/kardex', { params }),
};

export const cuentaContableService = {
  getAll:  ()          => api.get('/api/contabilidad/cuentas/list'),
  getById: (id)        => api.get(`/api/contabilidad/cuentas/detail/${id}`),
  create:  (data)      => api.post('/api/contabilidad/cuentas/create', data),
  update:  (id, data)  => api.put(`/api/contabilidad/cuentas/update/${id}`, data),
  delete:  (id)        => api.delete(`/api/contabilidad/cuentas/delete/${id}`),
};

export const siscontService = {
  getAll:   ()         => api.get('/api/contabilidad/siscont/conexiones/list'),
  getById:  (id)       => api.get(`/api/contabilidad/siscont/conexiones/detail/${id}`),
  create:   (data)     => api.post('/api/contabilidad/siscont/conexiones/create', data),
  update:   (id, data) => api.put(`/api/contabilidad/siscont/conexiones/update/${id}`, data),
  delete:   (id)       => api.delete(`/api/contabilidad/siscont/conexiones/delete/${id}`),
  probar:       (id)   => api.post(`/api/contabilidad/siscont/conexiones/test/${id}`),
  probarDatos:  (data) => api.post('/api/contabilidad/siscont/conexiones/test-datos', data),
};

export const almacenService = {
  getAll: () => api.get('/api/almacenes/list'),
  getById: (id) => api.get(`/api/almacenes/detail/${id}`),
  create: (data) => api.post('/api/almacenes/create', data),
  update: (id, data) => api.put(`/api/almacenes/update/${id}`, data),
  delete: (id) => api.delete(`/api/almacenes/delete/${id}`),
  // stock disponible del artículo en el almacén a la fecha (considera PE − PS − GR)
  getStockBulk: (id_almacen, id_articulos, fecha, id_parte_salida_excluir = null) =>
    api.post('/api/almacenes/stock-bulk', { id_almacen, id_articulos, fecha, id_parte_salida_excluir }),
};

export const requerimientoService = {
  getAll: () => api.get('/api/requerimientos/list'),
  getById: (id) => api.get(`/api/requerimientos/detail/${id}`),
  create: (data) => api.post('/api/requerimientos/create', data),
  update: (id, data) => api.put(`/api/requerimientos/update/${id}`, data),
  aprobar: (id, data) => api.put(`/api/requerimientos/aprobar/${id}`, data),
  duplicar: (id) => api.post(`/api/requerimientos/duplicar/${id}`),
  delete: (id) => api.delete(`/api/requerimientos/delete/${id}`),
  getSeguimiento: (id) => api.get(`/api/requerimientos/seguimiento/${id}`),
  getSeguimientoDetallado: (id) => api.get(`/api/requerimientos/seguimiento-detallado/${id}`),
  importGrupo: (data) => api.post('/api/requerimientos/import-grupo', data),
  deleteBulk: (ids) => api.post('/api/requerimientos/delete-bulk', { ids }),
};

export const serieModuloService = {
  getAll: () => api.get('/api/series/modulos/list'),
  create: (data) => api.post('/api/series/modulos/create', data),
  update: (id, data) => api.put(`/api/series/modulos/update/${id}`, data),
  delete: (id) => api.delete(`/api/series/modulos/delete/${id}`),
};

export const serieService = {
  getAll: (codigoModulo, idSucursal, incluirInactivas) => api.get('/api/series/list', { params: {
    ...(codigoModulo ? { codigo_modulo: codigoModulo } : {}),
    ...(idSucursal ? { id_sucursal: idSucursal } : {}),
    ...(incluirInactivas ? { incluir_inactivas: true } : {}),
  } }),
  getById: (id) => api.get(`/api/series/detail/${id}`),
  create: (data) => api.post('/api/series/create', data),
  update: (id, data) => api.put(`/api/series/update/${id}`, data),
  delete: (id) => api.delete(`/api/series/delete/${id}`),
  getCorrelativo: (id) => api.get(`/api/series/correlativo/${id}`),
};

export const formasPagoService = {
  getAll: () => api.get('/api/tablas/formas-pago/list'),
  create: (data) => api.post('/api/tablas/formas-pago/create', data),
  update: (id, data) => api.put(`/api/tablas/formas-pago/update/${id}`, data),
  delete: (id) => api.delete(`/api/tablas/formas-pago/delete/${id}`),
};

export const ubigeoService = {
  getAll: () => api.get('/api/tablas/ubigeos/list'),
  create: (data) => api.post('/api/tablas/ubigeos/create', data),
  update: (id, data) => api.put(`/api/tablas/ubigeos/update/${id}`, data),
  delete: (id) => api.delete(`/api/tablas/ubigeos/delete/${id}`),
};

export const parteEntradaService = {
  getAll: () => api.get('/api/partes-entrada/list'),
  delete: (id) => api.delete(`/api/partes-entrada/delete/${id}`),
  update: (id, data) => api.put(`/api/partes-entrada/update/${id}`, data),
  getTrazabilidad: (id) => api.get(`/api/partes-entrada/trazabilidad/${id}`),
  getOcsParaPe: () => api.get('/api/partes-entrada/ocs-para-pe'),
  getListDesdeOc: () => api.get('/api/partes-entrada/list-desde-oc'),
  getNextNumero: (id_almacen) => api.get(`/api/partes-entrada/next-numero/${id_almacen}`),
  create: (data) => api.post('/api/partes-entrada/create', data),
  getById: (id) => api.get(`/api/partes-entrada/detail/${id}`),
  getOcsAprobadas: () => api.get('/api/partes-entrada/ocs-aprobadas'),
  getOcItems: (id_orden_compra) => api.get(`/api/partes-entrada/oc-items/${id_orden_compra}`),
  getAtencionOc: (id_orden_compra) => api.get(`/api/partes-entrada/atencion-oc/${id_orden_compra}`),
  getListParaPs: () => api.get('/api/partes-entrada/list-para-ps'),
  getPeItemsParaPs: (id_parte_entrada) => api.get(`/api/partes-entrada/pe-items-para-ps/${id_parte_entrada}`),
  getGrsPendientes: () => api.get('/api/partes-entrada/grs-pendientes-recepcion'),
  getGrItems: (id_guia) => api.get(`/api/partes-entrada/gr-items/${id_guia}`),
};

export const tiposMovimientoService = {
  getAll: () => api.get('/api/tipos-movimiento/list'),
  getById: (id) => api.get(`/api/tipos-movimiento/detail/${id}`),
  create: (data) => api.post('/api/tipos-movimiento/create', data),
  update: (id, data) => api.put(`/api/tipos-movimiento/update/${id}`, data),
  delete: (id) => api.delete(`/api/tipos-movimiento/delete/${id}`),
};

export const ordenCompraService = {
  getAll: () => api.get('/api/ordenes-compra/list'),
  getById: (id) => api.get(`/api/ordenes-compra/detail/${id}`),
  create: (data) => api.post('/api/ordenes-compra/create', data),
  update: (id, data) => api.put(`/api/ordenes-compra/update/${id}`, data),
  aprobar: (id, data) => api.put(`/api/ordenes-compra/aprobar/${id}`, data),
  duplicar: (id) => api.post(`/api/ordenes-compra/duplicar/${id}`),
  delete: (id) => api.delete(`/api/ordenes-compra/delete/${id}`),
  // Correlación RQ ↔ OC
  getRqsAprobados: () => api.get('/api/ordenes-compra/rqs-aprobados'),
  getRqItems: (ids) => api.post('/api/ordenes-compra/rq-items', { ids }),
  getAtencionRq: (id) => api.get(`/api/ordenes-compra/atencion-rq/${id}`),
  toggleRevisado: (idDetalle, revisado) => api.put(`/api/ordenes-compra/detalle/revisado/${idDetalle}`, { revisado }),
  importGrupo: (data) => api.post('/api/ordenes-compra/import-grupo', data),
  deleteBulk: (ids) => api.post('/api/ordenes-compra/delete-bulk', { ids }),
};

export const parteSalidaService = {
  getAll: () => api.get('/api/partes-salida/list'),
  getNextNumero: (id_almacen) => api.get(`/api/partes-salida/next-numero/${id_almacen}`),
  create: (data) => api.post('/api/partes-salida/create', data),
  getById: (id) => api.get(`/api/partes-salida/detail/${id}`),
  update: (id, data) => api.put(`/api/partes-salida/update/${id}`, data),
  delete: (id) => api.delete(`/api/partes-salida/delete/${id}`),
  getTrazabilidad: (id) => api.get(`/api/partes-salida/trazabilidad/${id}`),
};

export const datosGeneralesService = {
  get: () => api.get('/api/entidades/datos-generales'),
  save: (data) => api.put('/api/entidades/datos-generales', data),
};

export const vehiculoService = {
  getAll: () => api.get('/api/entidades/vehiculos/list'),
  create: (data) => api.post('/api/entidades/vehiculos/create', data),
  update: (id, data) => api.put(`/api/entidades/vehiculos/update/${id}`, data),
  delete: (id) => api.delete(`/api/entidades/vehiculos/delete/${id}`),
};

export const guiaRemisionService = {
  getAll: () => api.get('/api/guias-remision/list'),
  getById: (id) => api.get(`/api/guias-remision/detail/${id}`),
  create: (data) => api.post('/api/guias-remision/create', data),
  update: (id, data) => api.put(`/api/guias-remision/update/${id}`, data),
  delete: (id) => api.delete(`/api/guias-remision/delete/${id}`),
  // GR desde PE
  getPesParaGr: (idSucursal) => api.get('/api/guias-remision/pes-para-gr', { params: idSucursal ? { id_sucursal: idSucursal } : {} }),
  getPeItems: (id_parte_entrada) => api.get(`/api/guias-remision/pe-items/${id_parte_entrada}`),
  // GR desde RQ
  getRqsParaGr: () => api.get('/api/guias-remision/rqs-para-gr'),
  getRqItems: (id_requerimiento) => api.get(`/api/guias-remision/rq-items/${id_requerimiento}`),
};

export default api;
